import { Controller } from "@hotwired/stimulus";
import Logger from '../lib/logger'

export default class extends Controller {
  static targets = ["filterForm", "advancedFilters", "exportOptions"];
  static values = { clearPath: String, allExpanded: Boolean };

  connect() {
    Logger.debug("History filtering controller connected");

    // Initialize tooltips if Bootstrap is available
    this.initializeTooltips();

    // Moved from initialize() (see below) — initialize() runs once per instantiation, not on
    // every connect, so a listener added there and removed in disconnect() would already be
    // gone by the time a later reconnect's disconnect tried to remove it again. Storing the
    // bound handler here also fixes the original leak: disconnect() removed
    // `this.handleKeydown`, a property that was never actually assigned — the real inline-arrow
    // listener was never removed at all.
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    document.addEventListener('keydown', this.boundHandleKeydown);
  }

  handleKeydown(e) {
    // Ctrl/Cmd + F to focus search
    if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
      const searchInput = document.querySelector('input[name="search"]');
      if (searchInput) {
        e.preventDefault();
        searchInput.focus();
      }
    }

    // Escape to clear filters
    if (e.key === 'Escape') {
      this.clearAllFilters();
    }
  }

  initializeTooltips() {
    if (typeof window.initializeTooltips === 'function') {
      window.initializeTooltips()
    } else if (typeof bootstrap !== 'undefined' && bootstrap.Tooltip) {
      // Fallback if global initializer is not yet ready
      const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
      tooltipTriggerList.forEach(tooltipTriggerEl => {
        if (!bootstrap.Tooltip.getInstance(tooltipTriggerEl)) {
          new bootstrap.Tooltip(tooltipTriggerEl, { container: 'body' });
        }
      });
    }
  }

  // Toggle advanced filters section
  toggleAdvancedFilters() {
    this.advancedFiltersTarget.classList.toggle('show');
    
    // Update button text
    const toggleButton = document.querySelector('[data-bs-target="#advanced-filters"]');
    if (toggleButton) {
      const icon = toggleButton.querySelector('i');
      const text = toggleButton.childNodes[2]; // Text node after icon
      if (this.advancedFiltersTarget.classList.contains('show')) {
        icon.className = 'fas fa-chevron-up me-1';
        text.textContent = ' Hide';
      } else {
        icon.className = 'fas fa-chevron-down me-1';
        text.textContent = ' Advanced';
      }
    }
  }

  // Toggle export options section
  toggleExportOptions() {
    this.exportOptionsTarget.classList.toggle('show');
    
    // Update button text
    const toggleButton = document.querySelector('[data-bs-target="#export-options"]');
    if (toggleButton) {
      const icon = toggleButton.querySelector('i');
      const text = toggleButton.childNodes[2]; // Text node after icon
      if (this.exportOptionsTarget.classList.contains('show')) {
        icon.className = 'fas fa-chevron-up me-1';
        text.textContent = ' Hide';
      } else {
        icon.className = 'fas fa-chevron-down me-1';
        text.textContent = ' Export';
      }
    }
  }

  // Clear all filters and reset form
  clearAllFilters() {
    // Was `this.data.get('clearPath')` — `this.data` is not a public Stimulus 3 controller
    // API (it lives on the internal scope object); use the generated value accessor instead.
    if (confirm('Are you sure you want to clear all filters?')) {
      window.location.href = this.clearPathValue || '/';
    }
  }

  // Apply filters with validation
  applyFilters() {
    const form = this.filterFormTarget;
    
    // Validate date range
    const fromDate = form.querySelector('input[name="date_from"]');
    const toDate = form.querySelector('input[name="date_to"]');
    
    if (fromDate && toDate) {
      const fromValue = fromDate.value;
      const toValue = toDate.value;
      
      if (fromValue && toValue) {
        const from_date = new Date(fromValue);
        const to_date = new Date(toValue);
        
        if (from_date > to_date) {
          alert('Date from cannot be after date to');
          return false;
        }
      }
    }
    
    // Submit the form
    form.submit();
  }

  // Search functionality with debounce
  search(event) {
    const searchTerm = event.target.value;
    
    // Debounce the search
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      if (searchTerm.length > 2 || searchTerm.length === 0) {
        // Add search parameter to form and submit
        const searchInput = document.querySelector('input[name="search"]');
        if (searchInput) {
          searchInput.value = searchTerm;
          this.applyFilters();
        }
      }
    }, 300);
  }

  // Expand/collapse all bundles
  toggleAllBundles(event) {
    event.preventDefault();
    
    // Was `this.data.get('allExpanded')` (same non-public-API issue as clearAllFilters above).
    const isExpanding = !this.allExpandedValue;
    
    // Update all bundle sections
    const bundleHeaders = document.querySelectorAll('[data-bs-toggle="collapse"][data-bs-target^="#bundle-"]');
    bundleHeaders.forEach(header => {
      const targetId = header.getAttribute('data-bs-target');
      const collapseElement = document.querySelector(targetId);
      
      if (collapseElement) {
        if (isExpanding) {
          collapseElement.classList.add('show');
        } else {
          collapseElement.classList.remove('show');
        }
      }
    });
    
    // Update all quote sections
    const quoteHeaders = document.querySelectorAll('[data-bs-toggle="collapse"][data-bs-target^="#quote-"]');
    quoteHeaders.forEach(header => {
      const targetId = header.getAttribute('data-bs-target');
      const collapseElement = document.querySelector(targetId);
      
      if (collapseElement) {
        if (isExpanding) {
          collapseElement.classList.add('show');
        } else {
          collapseElement.classList.remove('show');
        }
      }
    });
    
    // Update button state
    this.allExpandedValue = isExpanding;
  }

  // Export functionality with loading state
  exportData(event) {
    // Was `exportData(format)` reading the bare global `event` — the actual Event object
    // Stimulus passes as the action's argument was never bound to a parameter, so `format`
    // silently *was* the event, and `event.target` referenced the unreliable legacy
    // `window.event` global instead. Named the parameter correctly; `format` isn't otherwise
    // used in this method, so nothing else needs to change.
    const button = event.target;
    const originalText = button.innerHTML;
    
    // Show loading state
    button.disabled = true;
    button.innerHTML = '<i class="fas fa-spinner fa-spin me-1"></i> Exporting...';
    
    // Simulate export delay (remove in production)
    setTimeout(() => {
      button.disabled = false;
      button.innerHTML = originalText;
      
      // The actual export will happen through the Rails link
    }, 1000);
  }

  // Format change details display
  formatChangeDetails(version) {
    const changes = version.object_changes;
    
    if (!changes) return 'No changes data';
    
    try {
      const parsedChanges = JSON.parse(changes);
      
      if (typeof parsedChanges === 'object' && parsedChanges !== null) {
        const fieldNames = Object.keys(parsedChanges);
        
        if (fieldNames.length <= 3) {
          // Was `field.humanize()` — that's a Rails/ActiveSupport inflector method, plain JS
          // strings have no such method.
          return fieldNames.map(field => this.humanize(field)).join(', ');
        } else {
          return `${fieldNames.length} fields changed`;
        }
      }
    } catch (e) {
      return 'Changes detected';
    }
    
    return 'Multiple changes';
  }

  // Minimal Rails-style humanize: underscores to spaces, capitalize the first letter.
  humanize(str) {
    const withSpaces = str.replace(/_/g, ' ');
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
  }

  // Animate version rows on load
  animateVersionRows() {
    const versionRows = document.querySelectorAll('[data-version-row]');
    
    versionRows.forEach((row, index) => {
      // Stagger animation
      setTimeout(() => {
        row.style.opacity = '0';
        row.style.transform = 'translateY(10px)';
        
        // Trigger reflow
        void row.offsetWidth;
        
        // Animate in
        row.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
        row.style.opacity = '1';
        row.style.transform = 'translateY(0)';
      }, index * 50);
    });
  }

  // Initialize on page load
  initialize() {
    this.animateVersionRows();
    // Keyboard shortcut listener moved to connect()/disconnect() — see connect() above for why.
  }

  // Disconnect and cleanup
  disconnect() {
    clearTimeout(this.searchTimeout);

    // Was `this.handleKeydown` — a property that was never assigned, so this never actually
    // removed anything; the real listener (an inline arrow in initialize()) leaked on every
    // connect. Now removes the exact bound reference connect() added.
    document.removeEventListener('keydown', this.boundHandleKeydown);
  }
}